import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import { notification } from "antd";

const unauthorizedCode = [400, 401, 403];

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Config
const TOKEN_PAYLOAD_KEY = "authorization";
const REFRESH_TOKEN_ENDPOINT = "/jwt/refresh/"; // Update with your refresh token endpoint

// Function to refresh access token
const refreshAccessToken = async () => {
  try {
    const refresh = localStorage.getItem(REFRESH_TOKEN);
    if (!refresh) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(
      `${API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}`,
      {
        refresh,
      }
    );

    const newAccessToken = response.data.accessToken;
    localStorage.setItem(AUTH_TOKEN, newAccessToken);
    return newAccessToken;
  } catch (error) {
    throw error;
  }
};

// Function to format error messages
const getErrorMessage = (error) => {
  if (error.response) {
    if (typeof error.response.data?.message === "string") {
      return error.response.data.message;
    }
    if (typeof error.response.data === "string") {
      return error.response.data;
    }
    if (typeof error.response.data === "object") {
      // Flatten object into a readable string
      return Object.entries(error.response.data)
        .map(
          ([key, value]) =>
            `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
        )
        .join(" | ");
    }
  }
  return error.message || "An unknown error occurred";
};

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    notification.error({
      message: "Error",
      description: "Request failed. Please try again.",
    });
    return Promise.reject(error);
  }
);

// API response interceptor
service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${newAccessToken}`;

        return service(originalRequest); // Retry original request with new token
      } catch (err) {
        notification.error({
          message: "Authentication Failed",
          description: "Please login again",
        });
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        store.dispatch(signOutSuccess());
        return Promise.reject(err);
      }
    }

    const errorMessage = getErrorMessage(error);

    notification.error({
      description: errorMessage,
    });

    return Promise.reject(error);
  }
);

export default service;
